/**
 * Converts a DER-encoded ECDSA P-256 signature to raw IEEE P1363 form
 * (r ‖ s, 64 bytes).
 *
 * `crypto.subtle.verify` accepts only the raw form, while the standard ECDSA
 * encoding produced by OpenSSL, Python's cryptography and the JVM's Signature is
 * DER — so every WebCrypto verification path needs this conversion.
 *
 * DER trims leading zero bytes and prepends one when the high bit is set, so r
 * and s arrive at 32 bytes give or take one; each is right-aligned into its
 * fixed 32-byte half here.
 */
export function derToRaw(
  der: Uint8Array<ArrayBuffer>,
): Uint8Array<ArrayBuffer> {
  let i = 2;
  if (der[1] === 0x81) {
    i = 3;
  }
  i++;
  const rLen = der[i++];
  const r = der.slice(i, i + rLen);
  i += rLen;
  i++;
  const sLen = der[i++];
  const s = der.slice(i, i + sLen);
  const out = new Uint8Array(new ArrayBuffer(64));
  out.set(r.slice(-32), 32 - Math.min(r.length, 32));
  out.set(s.slice(-32), 64 - Math.min(s.length, 32));
  return out;
}
